import { BusinessRegistrationService } from '../../../../_services/BusinessRegistration.Service';
import { adminService } from '../../../../_services/admin.service';
import {
  BR_STATUS,
  getItemStatusCode,
  isBrItemExpired,
} from '../../../businessDocuments/utils/brDocument';
import {
  isBrAdminDraftBarMode,
  mapAdminProcessErrorMessage,
  mapApprovalRejectTemplatesToFrf,
  mapRejectReasonByApprovalRowsToFrf,
} from '../utils/brAdmin';
import {
  getProceedButtonState,
} from '../utils/brAdminProceed';
import { invalidateKybAccessGateCache } from '../../../../constants/businessRegistration';

/**
 * 管理端 BR：审批草稿、确认弹窗、AdminProcess。
 * 驳回转换唯一标准：udf_GetAliyunRejectLabel + GetRejectReasonByApprovalId。
 * Template：优先 GetRejectTemplatesByApprovalId；空则同 ApprovalId 走 GetRejectReasonByApprovalId。
 * 预填：hydrateRejectReasonsFromApproval → GetRejectReasonByApprovalId。
 */
export default {
  data() {
    return {
      drafts: {},
      processingId: null,
      proceedLockedIds: {},
      proceedUnlockedIds: {},
      showConfirm: false,
      showAlert: false,
      alertMessage: '',
      pendingAction: null,
      rejectTemplatesByApprovalId: {},
    };
  },
  computed: {
    confirmTitleHtml() {
      const action = this.pendingAction?.action;
      if (action === 'REJECT') {
        return this.$t('lbl_EMPBE_BR_Admin_ConfirmRejectTitle');
      }
      return this.$t('lbl_EMPBE_BR_Admin_ConfirmApproveTitle');
    },
    confirmBodyHtml() {
      const action = this.pendingAction?.action;
      const item = this.pendingAction?.item;
      if (action === 'REJECT') {
        return this.$t(this.resolveRejectConfirmBodyKey(item));
      }
      return this.$t(this.resolveApproveConfirmBodyKey(item));
    },
    confirmOkLabel() {
      const action = this.pendingAction?.action;
      if (action === 'REJECT') {
        return this.$t('lbl_EMPBE_BR_Admin_ConfirmRejectOk');
      }
      return this.$t('lbl_EMPBE_BR_Admin_ConfirmApproveOk');
    },
    confirmOkVariant() {
      return this.pendingAction?.action === 'REJECT' ? 'reject' : 'approve';
    },
  },
  methods: {
    resolveApproveConfirmBodyKey(item) {
      const code = getItemStatusCode(item);
      if (code === BR_STATUS.REJECTED) {
        return 'lbl_EMPBE_BR_Admin_ConfirmApproveBody_FromRejected';
      }
      if (code === BR_STATUS.PENDING_MANUAL) {
        return 'lbl_EMPBE_BR_Admin_ConfirmApproveBody_FromPendingManual';
      }
      return 'lbl_EMPBE_BR_Admin_ConfirmApproveBody_FromPending';
    },
    resolveRejectConfirmBodyKey(item) {
      const code = getItemStatusCode(item);
      if (code === BR_STATUS.REJECTED) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_UpdateReason';
      }
      if (this.isPendingEffectiveApprovedItem?.(item)) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromNotYetEffective';
      }
      if (this.isInEffectApprovedItem?.(item)) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromApproved';
      }
      if (code === BR_STATUS.PENDING_MANUAL) {
        return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromPendingManual';
      }
      return 'lbl_EMPBE_BR_Admin_ConfirmRejectBody_FromPending';
    },
    isDraftBarMode(item) {
      return isBrAdminDraftBarMode(item);
    },
    clearRejectTemplateCaches() {
      this.rejectTemplatesByApprovalId = {};
    },
    getRejectTemplatesForItem(item) {
      const approvalId = Number(item?.approvalId) || 0;
      if (approvalId > 0 && this.rejectTemplatesByApprovalId[approvalId]) {
        return this.rejectTemplatesByApprovalId[approvalId];
      }
      return [];
    },
    async prefetchRejectTemplates(items) {
      const ids = [...new Set(
        (Array.isArray(items) ? items : [])
          .filter((item) => this.shouldShowAdminActions?.(item))
          .map((item) => Number(item?.approvalId) || 0)
          .filter((id) => id > 0),
      )];
      if (!ids.length) {
        this.rejectTemplatesByApprovalId = {};
        return;
      }
      await Promise.all(ids.map((id) => this.loadRejectTemplatesForApprovalId(id)));
    },
    async loadRejectTemplatesForApprovalId(approvalId) {
      const id = Number(approvalId) || 0;
      if (id <= 0 || Object.prototype.hasOwnProperty.call(this.rejectTemplatesByApprovalId, id)) {
        return;
      }
      try {
        const res = await adminService.GetRejectTemplatesByApprovalId(id);
        const mapped = res?.isSuccessful
          ? mapApprovalRejectTemplatesToFrf(Array.isArray(res.data) ? res.data : [])
          : [];
        if (mapped.length) {
          this.$set(this.rejectTemplatesByApprovalId, id, mapped);
          return;
        }
      } catch (e) {
        console.error('loadRejectTemplatesForApprovalId', e);
      }
      /* 与预填同源：GetRejectReasonByApprovalId（udf 已转好的 RejectedReason） */
      try {
        const res = await adminService.GetRejectReasonByApprovalId(id);
        const mapped = res?.isSuccessful
          ? mapRejectReasonByApprovalRowsToFrf(Array.isArray(res.data) ? res.data : [])
          : [];
        this.$set(this.rejectTemplatesByApprovalId, id, mapped);
        return;
      } catch (e) {
        console.error('loadRejectTemplatesForApprovalId fallback', e);
      }
      this.$set(this.rejectTemplatesByApprovalId, id, []);
    },
    ensureDraft(approvalId) {
      if (!this.drafts[approvalId]) {
        this.$set(this.drafts, approvalId, {
          rejectSelected: false,
          /** FRF 驳回理由（与公司资料 FieldRejectionFeedback value 同口径） */
          reasonText: '',
          reasonError: false,
        });
      }
      return this.drafts[approvalId];
    },
    clearDraft(approvalId) {
      if (this.drafts[approvalId]) {
        this.$delete(this.drafts, approvalId);
      }
    },
    resetProceedLockState() {
      this.proceedLockedIds = {};
      this.proceedUnlockedIds = {};
    },
    getProceedSessionFlags() {
      return {
        proceedLockedIds: this.proceedLockedIds,
        proceedUnlockedIds: this.proceedUnlockedIds,
      };
    },
    /**
     * @param {object|string|number} itemOrId 优先传列表 item（含 statusCode）；仅传 id 时只能看会话锁
     * @returns {boolean} true = 按钮应隐藏（不可点）
     */
    isProceedLocked(itemOrId) {
      return getProceedButtonState(itemOrId, this.getProceedSessionFlags()) === 'hidden';
    },
    markProceedLocked(approvalId) {
      if (approvalId == null || approvalId === '') {
        return;
      }
      const key = String(approvalId);
      this.$delete(this.proceedUnlockedIds, key);
      this.$set(this.proceedLockedIds, key, true);
    },
    /** 用户改了草稿：取消隐藏，允许再点「处理」 */
    unlockProceed(approvalId) {
      const key = String(approvalId || '');
      if (!key) {
        return;
      }
      this.$delete(this.proceedLockedIds, key);
      this.$set(this.proceedUnlockedIds, key, true);
    },
    /** FRF @update:rejected → 勾选/取消驳回 */
    setRejectSelected(item, rejected) {
      const approvalId = item?.approvalId;
      if (!approvalId) return;
      const draft = this.ensureDraft(approvalId);
      const willSelect = !!rejected;
      const statusCode = getItemStatusCode(item);
      // 已驳回：保持勾选，仅允许改理由后点「继续」更新
      if (!willSelect && statusCode === BR_STATUS.REJECTED) {
        draft.rejectSelected = true;
        return;
      }
      if (!willSelect && !this.isDraftBarMode(item)) {
        this.clearDraft(approvalId);
        this.markProceedLocked(approvalId);
        return;
      }
      draft.rejectSelected = willSelect;
      draft.reasonError = false;
      this.unlockProceed(approvalId);
      if (!willSelect) {
        return;
      }
      if (!this.isDraftBarMode(item)) {
        draft.reasonText = '';
        return;
      }
      if (!String(draft.reasonText || '').trim()) {
        this.initRejectedDraft?.(item);
        draft.rejectSelected = true;
      }
    },
    /** FRF @input → 理由正文 */
    setRejectReasonText(approvalId, value) {
      const draft = this.ensureDraft(approvalId);
      draft.reasonText = value == null ? '' : String(value);
      draft.reasonError = false;
      this.unlockProceed(approvalId);
    },
    clearRejectReasonError(approvalId) {
      const draft = this.ensureDraft(approvalId);
      draft.reasonError = false;
    },
    validateRejectDraft(draft) {
      const text = String(draft?.reasonText || '').trim();
      if (!text) {
        draft.reasonError = true;
        this.openAlert(this.$t('lbl_EMPBE_BR_Admin_RejectReasonRequired'));
        return false;
      }
      draft.reasonError = false;
      return true;
    },
    onProceed(item) {
      if (this.isProceedLocked(item)) {
        return;
      }
      const draft = this.ensureDraft(item.approvalId);
      const statusCode = getItemStatusCode(item);
      // 审批中(P)：等阿里云机器审，禁止人工通过/驳回（操作栏亦已隐藏）
      if (statusCode === BR_STATUS.PENDING) {
        return;
      }
      /* 过期单不可「通过」：避免同号作废冲掉有效证；可驳回 */
      if (!draft.rejectSelected && isBrItemExpired(item)) {
        this.openAlert(this.$t('lbl_EMPBE_BR_Admin_CannotApproveExpired'));
        return;
      }
      if (this.isLastInEffectApprovedItem?.(item) && !this.canChangeToReject?.(item)
        && !this.isDraftBarMode(item)) {
        this.onRejectLastBlocked();
        return;
      }
      if (draft.rejectSelected) {
        if (!this.validateRejectDraft(draft)) return;
        this.pendingAction = {
          item,
          action: 'REJECT',
          rejectReason: String(draft.reasonText || '').trim(),
        };
        this.showConfirm = true;
        return;
      }
      if (!this.isDraftBarMode(item)) {
        return;
      }
      this.pendingAction = { item, action: 'APPROVE', rejectReason: '' };
      this.showConfirm = true;
    },
    onRejectLastBlocked() {
      this.openAlert(this.$t('lbl_EMPBE_BR_Admin_RejectLastBlocked'));
    },
    openAlert(message) {
      this.alertMessage = this.localizeAdminProcessError(message);
      this.showAlert = true;
    },
    localizeAdminProcessError(message) {
      return mapAdminProcessErrorMessage(message, this.$t.bind(this));
    },
    closeAlert() {
      this.showAlert = false;
      this.alertMessage = '';
    },
    closeConfirm() {
      this.showConfirm = false;
      this.pendingAction = null;
    },
    async confirmProcess() {
      const action = this.pendingAction;
      if (!action) {
        return;
      }
      // 防止确认框连点在 Vue 重绘前发出两次 AdminProcess
      if (this.processingId != null) {
        return;
      }
      this.processingId = action.item.approvalId;
      try {
        const res = await BusinessRegistrationService.AdminProcess({
          empCompanyId: this.empCompanyId,
          approvalId: action.item.approvalId,
          action: action.action,
          rejectReason: action.rejectReason,
        });
        if (!res?.isSuccessful) {
          const errMsg = (Array.isArray(res?.systemErrorMsg) && res.systemErrorMsg[0])
            || res?.msg
            || (typeof res?.data === 'string' ? res.data : '')
            || this.$t('lbl_EMPBE_BR_Admin_ProcessFailed');
          const localized = this.localizeAdminProcessError(String(errMsg));
          this.$toast?.error?.(localized);
          this.openAlert(localized);
          return;
        }
        invalidateKybAccessGateCache();
        if (action.action === 'APPROVE' && action.item.requestKind === 'NAME_CHANGE') {
          this.cachedCurrentNameEn = action.item.displayNameEn || this.cachedCurrentNameEn;
          this.cachedCurrentNameZh = action.item.displayNameZh || this.cachedCurrentNameZh;
        }
        if (action.action === 'REJECT') {
          const kind = String(action.item.requestKind || '').toUpperCase();
          if (kind === 'NAME_CHANGE' || kind === 'NAME_CHANGE_RESULT') {
            const prevEn = String(
              action.item.previousDisplayNameEn || action.item.PreviousDisplayNameEn || '',
            ).trim();
            const prevZh = String(
              action.item.previousDisplayNameZh || action.item.PreviousDisplayNameZh || '',
            ).trim();
            if (prevEn) this.cachedCurrentNameEn = prevEn;
            if (prevZh) this.cachedCurrentNameZh = prevZh;
          }
        }
        this.closeConfirm();
        this.markProceedLocked(action.item.approvalId);
        this.$toast?.success?.(
          action.action === 'REJECT'
            ? (
              getItemStatusCode(action.item) === BR_STATUS.REJECTED
                ? this.$t('lbl_EMPBE_BR_Admin_UpdateRejectReasonSuccess')
                : this.$t('lbl_EMPBE_BR_Admin_RejectSuccess')
            )
            : this.$t('lbl_EMPBE_BR_Admin_ApproveSuccess'),
        );
        await this.loadPending();
      } catch (e) {
        const errMsg = e?.message || this.$t('lbl_EMPBE_BR_Admin_ProcessFailed');
        const localized = this.localizeAdminProcessError(String(errMsg));
        this.$toast?.error?.(localized);
        this.openAlert(localized);
      } finally {
        this.processingId = null;
      }
    },
  },
};
